import { useState } from 'react';

export const DEFAULT_AVATAR = '/images/default-avatar.png';

const SIZE_MAP = {
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
  xl: 'h-20 w-20',
};

const INITIALS_SIZE_MAP: Record<string, string> = {
  xs: 'text-[8px]',
  sm: 'text-[10px]',
  md: 'text-xs',
  lg: 'text-sm',
  xl: 'text-xl',
};

export function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
}

interface UserAvatarProps {
  src?: string | null;
  name: string;
  size?: keyof typeof SIZE_MAP | string;
  className?: string;
  onClick?: () => void;
}

export function UserAvatar({ src, name, size = 'md', className = '', onClick }: UserAvatarProps) {
  const [imgError, setImgError] = useState(false);
  const sizeClass = SIZE_MAP[size as keyof typeof SIZE_MAP] ?? size;
  const isDefault = !src || imgError;

  if (isDefault) {
    const initialsSize = INITIALS_SIZE_MAP[size as string] ?? 'text-xs';
    return (
      <div
        onClick={onClick}
        className={`${sizeClass} rounded-full flex-shrink-0 relative overflow-hidden bg-sky-50 ${onClick ? 'cursor-pointer' : ''} ${className}`}
      >
        <img
          src={DEFAULT_AVATAR}
          alt={name}
          className="absolute inset-0 w-full h-full object-contain opacity-20 p-[18%]"
        />
        <span className={`absolute inset-0 flex items-center justify-center font-bold text-sky-700 ${initialsSize}`}>
          {getInitials(name)}
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      onError={() => setImgError(true)}
      onClick={onClick}
      className={`${sizeClass} rounded-full object-cover flex-shrink-0 ${onClick ? 'cursor-pointer' : ''} ${className}`}
    />
  );
}
